/**
 * ShareBattleButton — one-tap viral share of a battle/cypher/challenge.
 *
 * Behavior:
 * 1. If `highlightEndpoint` is provided, POSTs to fetch a 15s highlight MP4
 * URL, then tries the Web Share API with the actual MP4 file attached
 * (native sheet lets users post to IG/TikTok/Twitter directly).
 * 2. If share-with-file isn't supported (desktop, older Safari), falls back
 * to opening the MP4 in a new tab so users can right-click → save →
 * re-upload manually.
 * 3. If no highlightEndpoint, just shares the current page URL (legacy).
 *
 * Design: keep the API surface tiny so the same button drops into every
 * combat screen (Cypher reel, Battle result, PvP verdict).
 */
import React, { useState } from "react";
import axios from "axios";
import { API } from "@/auth";

export default function ShareBattleButton({
 battleUrl,
 title = "Watch this rap battle ",
 text = "Two rappers. AI judge. Real bars. Watch this cypher on Young Tongues.",
 compact = false,
 highlightEndpoint = null, // e.g. `/api/cypher/${id}/highlight-mp4`
 testid = "share-battle-btn",
}) {
 const [state, setState] = useState("idle"); // idle | rendering | copied
 const [status, setStatus] = useState("");

 const shareText = async () => {
 const url = battleUrl || (typeof window !== "undefined" ? window.location.href : "");
 if (typeof navigator !== "undefined" && navigator.share) {
 try { await navigator.share({ title, text, url }); return; } catch { /* fall through */ }
 }
 try {
 await navigator.clipboard.writeText(url);
 setState("copied"); setStatus("");
 setTimeout(() => setState("idle"), 2000);
 } catch {
 window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text + " " + url)}`, "_blank");
 }
 };

 const shareHighlight = async () => {
 setState("rendering");
 setStatus("Cooking your 15s highlight...");
 try {
 const { data } = await axios.post(`${API}${highlightEndpoint}`, {}, { timeout: 180000 });
 const mp4Url = `${process.env.REACT_APP_BACKEND_URL}${data.highlight_url}`;
 setStatus("");

 // Try Web Share API Level 2 (file sharing)
 try {
 const resp = await fetch(mp4Url);
 const blob = await resp.blob();
 const file = new File([blob], "young-tongues-battle.mp4", { type: "video/mp4" });
 if (navigator.canShare && navigator.canShare({ files: [file] })) {
 await navigator.share({ files: [file], title, text });
 setState("idle");
 return;
 }
 } catch { /* fall through to download */ }

 // Fallback: trigger download so user can post it manually
 const a = document.createElement("a");
 a.href = mp4Url;
 a.download = "young-tongues-battle.mp4";
 document.body.appendChild(a);
 a.click();
 a.remove();
 setState("copied");
 setStatus("Downloaded — post to IG/TikTok/Twitter!");
 setTimeout(() => { setState("idle"); setStatus(""); }, 4000);
 } catch (e) {
 setStatus("⚠ Highlight failed — sharing link instead");
 await shareText();
 setState("idle");
 setTimeout(() => setStatus(""), 3000);
 }
 };

 const handleClick = highlightEndpoint ? shareHighlight : shareText;

 const label = state === "rendering" ? "COOKING..." :
 state === "copied" ? "✔ READY" :
 highlightEndpoint ? (compact ? "SHARE HIGHLIGHT" : "SHARE 15s HIGHLIGHT") :
 (compact ? "SHARE" : "SHARE THIS BATTLE");

 return (
 <div className="flex flex-col items-end gap-1">
 <button
 data-testid={testid}
 onClick={handleClick}
 disabled={state === "rendering"}
 className={`bg-yt-yellow text-black font-mono tracking-widest hover:bg-white shadow-brutal disabled:opacity-50 disabled:cursor-wait ${compact ? "text-xs px-3 py-2" : "text-sm px-5 py-3"}`}
 >
 {label}
 </button>
 {status && <div className="font-mono text-[10px] text-yt-yellow bg-black/70 px-2 py-1 max-w-[220px] text-right">{status}</div>}
 </div>
 );
}
